/**
 * The creation gate (M7-03).
 *
 * Before a signed-in browser connects, it asks the API one question: is this
 * account a returning character, or a fresh arrival with nothing yet? The world
 * itself would answer it too — a join with no saved character *is* a new one —
 * but the client needs to know *before* it joins, so it can show the creation
 * screen rather than dropping an unnamed body into the hall. `/me` is that
 * question; it already exists (M7-01) and returns `character: null` for an
 * account that has never played.
 */

import { apiUrl } from '../config.js';

export interface Me {
  /** Whether this account already has a character in the world. */
  readonly hasCharacter: boolean;

  /** Their character's name, or the player name, or `null` for neither. */
  readonly name: string | null;
}

/**
 * Ask the API who this token belongs to, or `null` when it cannot say — an
 * expired token, a revoked account, or an API that will not answer. The caller
 * reads `null` as "this session is no good, send them back to the portal",
 * which is the safe default for a milestone whose whole point is that the game
 * is behind a sign-in.
 */
export async function fetchMe(token: string): Promise<Me | null> {
  let response: Response;
  try {
    response = await fetch(`${apiUrl()}/me`, {
      headers: { authorization: `Bearer ${token}` },
    });
  } catch {
    return null;
  }

  if (!response.ok) return null;

  const data = (await response.json().catch(() => null)) as {
    character?: { name?: string } | null;
    player?: { name?: string } | null;
  } | null;
  if (!data) return null;

  return {
    hasCharacter: data.character != null,
    name: data.character?.name ?? data.player?.name ?? null,
  };
}
